import React, {
  createContext,
  createRef,
  MutableRefObject,
  useState,
  useContext,
} from 'react'
import { SpendingBucket } from '../types'

export type DrawerOption = 'distribute'

export type Step = 1 | 2 | 3 | 4 | 5

type SpendingBucketRefs = {
  addBucketButton: MutableRefObject<HTMLButtonElement | null>
  addBucketHeader: MutableRefObject<HTMLHeadingElement | null>
  spendingBucketsCreateStep: MutableRefObject<HTMLDivElement | null>
  getStartedButton: MutableRefObject<HTMLButtonElement | null>
  bucketDetailsHeader: MutableRefObject<HTMLHeadingElement | null>
  distributeHeading: MutableRefObject<HTMLHeadingElement | null>
}

interface SpendingBucketViewProps {
  addingBucket: boolean
  createStep: Step
  createdBucket: SpendingBucket | null
  drawerExpanded: boolean
  showDistributeModal: boolean
  drawerSelection: DrawerOption
  editingBucket: boolean
  managingOverspend: boolean
  newBucketName: string
  selectedBucket: SpendingBucket | null
}

type SpendingBucketViewOptions = Partial<SpendingBucketViewProps>

interface SpendingBucketProviderProps {
  state: SpendingBucketViewProps
  refs: SpendingBucketRefs
  update: (options: SpendingBucketViewOptions) => void
}

const refs: SpendingBucketRefs = {
  addBucketButton: createRef<HTMLButtonElement | null>(),
  // TODO - change "Header" to "Heading" for all refs
  addBucketHeader: createRef<HTMLHeadingElement | null>(),
  spendingBucketsCreateStep: createRef<HTMLDivElement | null>(),
  getStartedButton: createRef<HTMLButtonElement | null>(),
  bucketDetailsHeader: createRef<HTMLHeadingElement | null>(),
  distributeHeading: createRef<HTMLHeadingElement | null>(),
}

export const SpendingBucketsViewContext = createContext<
  SpendingBucketProviderProps | undefined
>(undefined)

export const defaultSpendingBucketsView: SpendingBucketViewProps = {
  addingBucket: false,
  createStep: 1,
  createdBucket: null,
  drawerExpanded: false,
  showDistributeModal: false,
  drawerSelection: 'distribute',
  editingBucket: false,
  managingOverspend: false,
  newBucketName: '',
  selectedBucket: null,
}

export const SpendingBucketsViewProvider: React.FC<{
  initialValues?: SpendingBucketViewProps
  mockUpdate?: () => void
}> = ({
  children,
  initialValues = defaultSpendingBucketsView /* istanbul ignore next */,
  mockUpdate = undefined,
}) => {
  const [state, setState] = useState<SpendingBucketProviderProps>({
    state: initialValues,
    refs,
    // not worth testing this simple function
    update:
      mockUpdate ||
      /* istanbul ignore next */ ((options): void => {
        setState(prevState => {
          return { ...prevState, state: { ...prevState.state, ...options } }
        })
      }),
  })

  return (
    <SpendingBucketsViewContext.Provider value={state}>
      {children}
    </SpendingBucketsViewContext.Provider>
  )
}

export const useSpendingBucketsView = (): SpendingBucketProviderProps => {
  const context = useContext(SpendingBucketsViewContext)
  if (context === undefined) {
    throw new Error(
      'useSpendingBuckets must be used within a SpendingBucketsViewProvider',
    )
  }
  return context
}
